import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { LearningDialogComponent } from '../../components/dialogs/learning-dialog/learning-dialog.component';
import {
  AlTableBadgeComponent,
  AlTableBadgeVariant,
} from '../../components/features/table-badge/table-badge.component';
import { AlTableTextComponent } from '../../components/features/table-text/table-text.component';
import {
  AlTableColumn,
  AlTableRowAction,
} from '../../components/features/table/table.component';
import { AuthService, UserRoles } from '../../services/auth.service';
import { DozentService } from '../../services/dozent.service';
import {
  Learning,
  LearningInput,
  LearningSearchHit,
  LearningService,
} from '../../services/learning.service';
import { Tutor, TutorService, TutorSettingItem } from '../../services/tutor.service';

/**
 * Agent Learnings — serves BOTH scopes from one component, like guardrails.
 *
 *   /learnings                    client-wide learnings (every agent of the
 *                                 client, filtered by template)
 *   /tutors/:tutorId/learnings    this agent's learnings + the client-wide ones
 *                                 that apply to it, shown read-only
 *
 * The agent page also carries the two settings that switch the feature on
 * (`agent_learnings_enabled`, `agent_learnings_top_k`) and a test field that
 * runs exactly the search the agent runs. Those settings are hidden in the
 * AI configuration on purpose: one home per switch.
 *
 * Learnings are complementary to guardrails: guardrails PREVENT, learnings
 * IMPROVE — same menu neighbourhood, different mechanics.
 */
@Component({
  selector: 'app-learnings',
  templateUrl: './learnings.component.html',
  styleUrl: './learnings.component.scss',
})
export class LearningsComponent implements OnInit, OnDestroy {
  learnings: Learning[] = [];
  /** Cached result of the search filter — never a getter that builds a new
   *  array per change detection (CLAUDE.md rule 18: app-table would rebuild
   *  its subtree and swallow clicks). */
  filteredLearnings: Learning[] = [];
  total = 0;
  isLoading = false;

  tutorId: string | null = null;
  tutor?: Tutor;
  enabledSetting?: TutorSettingItem;
  topKSetting?: TutorSettingItem;

  columns: AlTableColumn[] = [];
  rowActions: AlTableRowAction[] = [];
  globalActions: { key: string; title: string; icon: string }[] = [];
  readonly pageSize = 25;

  searchText = '';

  testQuery = '';
  testRunning = false;
  testHits: LearningSearchHit[] | null = null;
  testLimit = 0;
  /** Simulated session course for the test; null = no course. */
  testCourseId: string | null = null;
  courses: { id: string; name: string }[] = [];

  reembedding = false;

  private subs: Subscription[] = [];

  constructor(
    private learningService: LearningService,
    private tutorService: TutorService,
    private auth: AuthService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private route: ActivatedRoute,
    private t: TranslateService,
    private dozentService: DozentService
  ) {}

  ngOnInit(): void {
    this.tutorId =
      this.route.snapshot.paramMap.get('tutorId') ??
      this.route.parent?.snapshot.paramMap.get('tutorId') ??
      null;

    if (this.tutorId) {
      // app-tutor-menu loads the agent and its settings; we read them here.
      this.subs.push(
        this.tutorService.tutors$.subscribe(() => {
          this.tutor = this.tutorService.findTutor(this.tutorId!);
        }),
        this.tutorService.tutorSettings$.subscribe((settings) => {
          this.enabledSetting = (settings || []).find(
            (s) => s.variable_name === 'agent_learnings_enabled'
          );
          this.topKSetting = (settings || []).find(
            (s) => s.variable_name === 'agent_learnings_top_k'
          );
        })
      );
    }

    this.buildTable();
    this.load();
    if (this.tutorId) {
      // Only for the test's course simulation; failing leaves "no course".
      this.dozentService.listCourses().subscribe({
        next: (rows: any[]) =>
          (this.courses = (rows || [])
            .map((c) => ({ id: String(c.id), name: String(c.course_name || c.name || c.id) }))
            .sort((a, b) => a.name.localeCompare(b.name))),
        error: () => (this.courses = []),
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get scope(): 'client' | 'bot' {
    return this.tutorId ? 'bot' : 'client';
  }

  /** Client-wide rows change every agent — writing them is admin/manager
   *  only (the backend enforces it; the page just does not offer it). */
  get canWriteClient(): boolean {
    const role = this.auth.accessTokenPayload$.value?.role;
    return (
      role === UserRoles.Admin ||
      role === UserRoles.Manager ||
      role === UserRoles.SuperAdmin
    );
  }

  get canWrite(): boolean {
    return this.scope === 'bot' || this.canWriteClient;
  }

  isInherited(row: Learning): boolean {
    return this.scope === 'bot' && !row.bot_id;
  }

  private isLocked(row: Learning): boolean {
    return this.isInherited(row) || (this.scope === 'client' && !this.canWriteClient);
  }

  get featureEnabled(): boolean {
    const v = this.enabledSetting?.value ?? this.enabledSetting?.default_value;
    return v === true || v === 'true';
  }

  get unsearchableCount(): number {
    return this.learnings.filter((l) => !l.embedded_at && (this.scope === 'client' || !this.isInherited(l))).length;
  }

  get truncated(): boolean {
    return this.total > this.learnings.length;
  }

  // ── Search ──

  onSearchChange(): void {
    this.applyFilter();
  }

  private applyFilter(): void {
    const q = this.searchText.trim().toLowerCase();
    this.filteredLearnings = !q
      ? this.learnings
      : this.learnings.filter((l) =>
          [l.topic, l.question, l.content, ...(l.tags || [])]
            .filter(Boolean)
            .some((f) => String(f).toLowerCase().includes(q))
        );
  }

  // ── Table ──

  private buildTable(): void {
    const inherited = this.t.instant('pages.learnings.inherited');
    this.columns = [
      {
        key: 'type',
        title: this.t.instant('pages.learnings.fields.type'),
        width: '11%',
        sortable: true,
        component: AlTableBadgeComponent,
        componentInputs: ({ value, row }) => ({
          label: this.t.instant(
            'pages.learnings.types.' + (value === 'rule' && row.always_apply ? 'ruleAlways' : value)
          ),
          variant: value === 'rule' ? 'primary' : 'muted',
          tooltip: value === 'rule' && row.always_apply
            ? this.t.instant('pages.learnings.hints.alwaysApply')
            : undefined,
        }),
      },
      {
        key: 'topic',
        kind: 'name',
        title: this.t.instant('pages.learnings.fields.topicOrQuestion'),
        width: '24%',
        sortable: true,
        component: AlTableTextComponent,
        componentInputs: ({ row }) => ({
          text: row.topic || row.question || '',
          max: 120,
          suffix: this.isInherited(row) ? inherited : undefined,
          suffixTooltip: this.isInherited(row)
            ? this.t.instant('pages.learnings.inheritedTooltip')
            : undefined,
        }),
      },
      {
        key: 'content',
        kind: 'text',
        title: this.t.instant('pages.learnings.fields.content'),
        width: '25%',
        component: AlTableTextComponent,
        componentInputs: ({ value }) => ({ text: value, max: 200 }),
      },
      {
        key: 'applies_to_templates',
        title: this.t.instant('pages.learnings.fields.appliesTo'),
        width: '13%',
        component: AlTableBadgeComponent,
        componentInputs: ({ row }) => ({
          label: this.scopeLabel(row),
          variant: row.bot_id ? 'muted' : 'primary',
          tooltip: this.scopeLabel(row),
        }),
      },
      {
        key: 'is_active',
        title: this.t.instant('words.status'),
        width: '10%',
        sortable: true,
        component: AlTableBadgeComponent,
        componentInputs: ({ row }) => this.statusBadge(row),
      },
      {
        // How often the agent's search returned it. 0 after weeks = phrased
        // past the questions (add anchors) or not needed.
        key: 'hit_count',
        kind: 'number',
        title: this.t.instant('pages.learnings.fields.hits'),
        width: '7%',
        sortable: true,
      },
      {
        key: 'embedded_at',
        title: this.t.instant('pages.learnings.fields.searchable'),
        width: '10%',
        component: AlTableBadgeComponent,
        componentInputs: ({ value }) => ({
          label: this.t.instant(value ? 'pages.learnings.searchable.yes' : 'pages.learnings.searchable.no'),
          variant: value ? 'success' : 'warning',
          tooltip: value ? undefined : this.t.instant('pages.learnings.searchable.noTooltip'),
        }),
      },
    ];

    const lockedTooltip = (row: Learning) =>
      this.isInherited(row)
        ? this.t.instant('pages.learnings.inheritedTooltip')
        : this.t.instant('pages.learnings.readOnlyTooltip');
    const locked = (row: Learning) => this.isLocked(row);

    this.rowActions = [
      {
        key: 'edit',
        title: this.t.instant('buttons.edit'),
        icon: 'edit-outline',
        disabled: locked,
        tooltip: (row: Learning) => (locked(row) ? lockedTooltip(row) : this.t.instant('buttons.edit')),
      },
      {
        key: 'toggle',
        title: this.t.instant('pages.learnings.toggleStatus'),
        icon: 'toggle-right-outline',
        isMultiAction: true,
        disabled: locked,
        tooltip: (row: Learning) =>
          locked(row) ? lockedTooltip(row) : this.t.instant('pages.learnings.toggleStatus'),
      },
      {
        key: 'delete',
        title: this.t.instant('buttons.delete'),
        icon: 'trash-outline',
        isMultiAction: true,
        disabled: locked,
        tooltip: (row: Learning) => (locked(row) ? lockedTooltip(row) : this.t.instant('buttons.delete')),
      },
    ] as AlTableRowAction[];

    this.globalActions = this.canWrite
      ? [{ key: 'create', title: this.t.instant('pages.learnings.add'), icon: 'plus-outline' }]
      : [];
  }

  /** Inactive < expired < review due < active: the badge shows the one
   *  thing an expert has to act on. */
  private statusBadge(row: Learning): { label: string; variant: AlTableBadgeVariant; tooltip?: string } {
    const today = new Date().toISOString().slice(0, 10);
    if (!row.is_active) {
      return { label: this.t.instant('pages.learnings.status.inactive'), variant: 'muted' };
    }
    if (row.valid_until && row.valid_until < today) {
      return {
        label: this.t.instant('pages.learnings.status.expired'),
        variant: 'danger',
        tooltip: this.t.instant('pages.learnings.status.expiredTooltip', { date: row.valid_until }),
      };
    }
    if (row.review_at && row.review_at <= today) {
      return {
        label: this.t.instant('pages.learnings.status.reviewDue'),
        variant: 'warning',
        tooltip: this.t.instant('pages.learnings.status.reviewDueTooltip', { date: row.review_at }),
      };
    }
    return { label: this.t.instant('pages.learnings.status.active'), variant: 'success' };
  }

  private scopeLabel(row: Learning): string {
    if (row.bot_id) return this.t.instant('pages.learnings.scope.thisAgent');
    if (!row.applies_to_templates?.length) return this.t.instant('pages.learnings.scope.allAgents');
    return row.applies_to_templates
      .map((code) => this.t.instant('enums.assistantTemplate.' + code))
      .join(', ');
  }

  // ── Data ──

  load(): void {
    this.isLoading = true;
    const req$ = this.tutorId
      ? this.learningService.listBot(this.tutorId)
      : this.learningService.listClient();
    req$.subscribe({
      next: (res) => {
        this.learnings = res.data;
        this.total = res.total;
        this.applyFilter();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastrService.danger(this.t.instant('pages.learnings.loadFailed'), '');
      },
    });
  }

  onTableEvent(payload: { event: string; data: any }): void {
    if (payload.event === 'globalAction') {
      this.openDialog();
      return;
    }
    if (payload.event === 'multiAction') {
      this.onMultiAction(payload.data?.action?.key, payload.data?.rows || []);
      return;
    }
    if (payload.event !== 'rowAction') return;
    const row = payload.data?.row as Learning;
    if (!row || this.isLocked(row)) return;
    switch (payload.data?.action?.key) {
      case 'edit':
        this.openDialog(row);
        break;
      case 'toggle':
        this.save(row, { is_active: !row.is_active });
        break;
      case 'delete':
        this.confirmDelete([row]);
        break;
    }
  }

  private onMultiAction(key: string, rows: Learning[]): void {
    const actionable = rows.filter((r) => !this.isLocked(r));
    if (!actionable.length) {
      this.toastrService.warning(this.t.instant('pages.learnings.bulkNothingEditable'), '');
      return;
    }
    if (key === 'delete') {
      this.confirmDelete(actionable);
    } else if (key === 'toggle') {
      // One target state for the whole selection — see guardrails page.
      const target = !actionable.some((r) => r.is_active);
      this.runAll(actionable.map((r) => this.update$(r, { is_active: target })), 'pages.learnings.updated');
    }
  }

  private update$(row: Learning, input: Partial<LearningInput>): Observable<unknown> {
    return this.tutorId
      ? this.learningService.updateBot(this.tutorId, row.id, input)
      : this.learningService.updateClient(row.id, input);
  }

  private delete$(row: Learning): Observable<unknown> {
    return this.tutorId
      ? this.learningService.deleteBot(this.tutorId, row.id)
      : this.learningService.deleteClient(row.id);
  }

  private runAll(reqs: Observable<unknown>[], successKey: string): void {
    this.isLoading = true;
    forkJoin(reqs).subscribe({
      next: () => {
        this.toastrService.success(this.t.instant(successKey), '');
        this.load();
      },
      error: () => {
        this.toastrService.danger(this.t.instant('pages.learnings.saveFailed'), '');
        this.load(); // partial success is possible — show the truth
      },
    });
  }

  private save(row: Learning, input: Partial<LearningInput>): void {
    this.update$(row, input).subscribe({
      next: () => this.load(),
      error: (err) => this.toastSaveError(err),
    });
  }

  private toastSaveError(err: any): void {
    const detail = err?.error?.detail;
    this.toastrService.danger(
      typeof detail === 'string' ? detail : this.t.instant('pages.learnings.saveFailed'),
      ''
    );
  }

  openDialog(row: Learning | null = null): void {
    if (row && this.isLocked(row)) return;
    if (!row && !this.canWrite) return;
    this.dialogService.open(LearningDialogComponent, {
      context: {
        learning: row,
        scope: this.scope,
        onConfirm: (input: LearningInput) => {
          const req$ = row
            ? this.update$(row, input)
            : this.tutorId
              ? this.learningService.createBot(this.tutorId, input)
              : this.learningService.createClient(input);
          req$.subscribe({
            next: (saved: any) => {
              this.toastrService.success(
                this.t.instant(row ? 'pages.learnings.updated' : 'pages.learnings.created'),
                ''
              );
              if (saved && saved.embedded_at === null) {
                this.toastrService.warning(this.t.instant('pages.learnings.searchable.noAfterSave'), '');
              }
              this.load();
            },
            error: (err) => this.toastSaveError(err),
          });
        },
      },
    });
  }

  private confirmDelete(rows: Learning[]): void {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.learnings.deleteTitle',
        message: this.t.instant('pages.learnings.deleteMessage', { count: rows.length }),
        onConfirm: () => {
          this.runAll(rows.map((r) => this.delete$(r)), 'pages.learnings.deleted');
          ref.close();
        },
      },
    });
  }

  // ── Re-embed ──

  reembed(): void {
    this.reembedding = true;
    const req$ = this.tutorId
      ? this.learningService.reembedBot(this.tutorId)
      : this.learningService.reembedClient();
    req$.subscribe({
      next: (res) => {
        this.reembedding = false;
        const key = res.failed ? 'pages.learnings.reembedPartial' : 'pages.learnings.reembedDone';
        this.toastrService[res.failed ? 'warning' : 'success'](this.t.instant(key, res), '');
        this.load();
      },
      error: () => {
        this.reembedding = false;
        this.toastrService.danger(this.t.instant('pages.learnings.reembedFailed'), '');
      },
    });
  }

  // ── Test ──

  runTest(): void {
    const q = this.testQuery.trim();
    if (!q || !this.tutorId) return;
    this.testRunning = true;
    this.learningService.searchTest(this.tutorId, q, this.testCourseId).subscribe({
      next: (res) => {
        this.testHits = res.data;
        this.testLimit = res.limit;
        this.testRunning = false;
      },
      error: () => {
        this.testRunning = false;
        this.testHits = null;
        this.toastrService.danger(this.t.instant('pages.learnings.test.failed'), '');
      },
    });
  }

  onSettingSaved(): void {
    // tutorSettings$ is refreshed by app-tutor-settings; nothing to do here
    // beyond re-evaluating `featureEnabled`, which the getter does.
  }

  trackHit = (i: number) => i;
}
